import { Box, Tooltip, useTheme } from '@mui/material';
import { StyledItemText, StyledTagItem } from './style';
import { TagType } from '../../types/types';
import { LabelTagBox } from '../label-tag-box';
import { convertSecondsToHours } from 'src/utilities';
import React, { useEffect, useRef, useState } from 'react';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { CheckmarkCircleFilledLargeIcon } from 'src/assets/icons/common/CheckmarkCircleFilledLargeIcon';
import { StopAlaramIcon } from 'src/assets/icons/StopAlarmIcon';
import { LabelingTypeEnum } from 'src/types';
import { UserDefinedValue } from 'src/types/controllers/controller';

type Props = {
  tag: TagType;
  isMobile: boolean;
  testId: string;
  liveDataComplete: any;
  isLiveDataWidget?: boolean;
  isLiftStationMobile?: boolean;
  isLiftStation?: boolean;
  isSCADA?: boolean;
};

export const TagItem = React.memo(
  ({
    tag,
    isMobile,
    testId,
    liveDataComplete,
    isLiveDataWidget,
    isLiftStationMobile,
    isLiftStation,
    isSCADA,
  }: Props) => {
    const liveDataTag = liveDataComplete?.tags?.find(
      (item: TagType) => item.id === (tag.tagId ?? tag.id)
    );

    const theme = useTheme();
    const isDataInSeconds = tag.rawUnit === 's';
    const greenColor = theme.palette.success.main;
    const redColor = theme.palette.error.main;
    const tagValue = Number(liveDataTag?.value || tag?.value);
    const displayName = liveDataTag?.name || tag?.displayName;
    const rawUnit = liveDataTag?.rawUnit || tag?.rawUnit;
    const displayedRawUnit = isDataInSeconds ? '(h)' : rawUnit && `(${rawUnit})`;
    const labellingType: LabelingTypeEnum = liveDataTag?.labbelingType || tag?.labellingType;
    const userDefinedValues: UserDefinedValue[] = tag?.advancedTagMapping?.userDefinedValues;
    const fallbackValue: string = liveDataTag?.fallbackValue || tag?.fallbackValue;

    const getTagCondition = (liveData: any, data: any, key: string) => {
      if (liveData && liveData.id) {
        return liveData[key];
      }
      return data[key];
    };

    const isColorCodingFalse = getTagCondition(liveDataTag, tag, 'hasColorCoding') === false;

    //Alarm tag
    const alarmTagOn = getTagCondition(liveDataTag, tag, 'indicator') === 'AlarmOn';
    const alarmTagOff = getTagCondition(liveDataTag, tag, 'indicator') === 'AlarmOff';

    //Alarm priority tag
    const alarmPriorityTag = getTagCondition(liveDataTag, tag, 'indicator');
    const isNotRestOfTheTags = !alarmTagOn && !alarmTagOff;

    //Is only "daily runtime"
    const isOnlyDailyRuntime =
      tag?.connectedToAssets?.length > 0 &&
      tag?.connectedToAssets?.every(
        (asset) => asset.assignedTagLabel.toLowerCase() === 'daily runtime'
      );
    let finalValue: number | null | string = tagValue;

    if (isOnlyDailyRuntime) {
      finalValue = Number(convertSecondsToHours(tagValue, 2).slice(0, -1));
    }

    //labelyingType
    const findUserDefinedItem = userDefinedValues?.find((item) => Number(item.value) === tagValue);

    if (labellingType === LabelingTypeEnum.LABEL) {
      if (!findUserDefinedItem) {
        finalValue = fallbackValue;
      } else {
        const name = findUserDefinedItem?.name;
        finalValue = name ?? fallbackValue;
      }
    }
    if (labellingType === LabelingTypeEnum.VALUE_LABEL) {
      if (!findUserDefinedItem) {
        finalValue = fallbackValue;
      } else {
        const name = findUserDefinedItem?.name;
        const value = findUserDefinedItem?.value;

        finalValue = `${value}-${name}`;
      }
    }

    //Trend for 'Level' tags
    const isOnlyLevel =
      tag?.connectedToAssets?.length > 0 &&
      tag?.connectedToAssets?.every((asset) => asset.assignedTagLabel.toLowerCase() === 'level');

    const prevTagValueRef = useRef<number | null>(null);
    const [trend, setTrend] = useState<'up' | 'down' | null>(null);

    useEffect(() => {
      if (!isOnlyLevel) return;
      if (tagValue != null && !isNaN(tagValue)) {
        if (prevTagValueRef.current !== null) {
          if (tagValue > prevTagValueRef.current) {
            setTrend('up');
          } else if (tagValue < prevTagValueRef.current) {
            setTrend('down');
          } else {
            setTrend(null);
          }
        }
        prevTagValueRef.current = tagValue;
      }
    }, [tagValue, isOnlyLevel]);

    const displayNameAndRawUnit = `${displayName}${displayedRawUnit ? `${displayedRawUnit}` : ''}`;

    let ComponentToRender = null;

    if (alarmTagOff) {
      ComponentToRender = (
        <Box data-sm={`checkmark-icon-${tag.id ? tag.id : tag.tagId}`}>
          <CheckmarkCircleFilledLargeIcon fill={greenColor} width={16} height={16} />
        </Box>
      );
    } else if (alarmTagOn) {
      ComponentToRender = (
        <Box data-sm={`stop-icon-${tag.id ? tag.id : tag.tagId}`}>
          <StopAlaramIcon width={16} height={16} fill={redColor} />
        </Box>
      );
    } else if (isColorCodingFalse) {
      ComponentToRender = (
        <LabelTagBox
          isLiftStationMobile={isLiftStationMobile}
          alarmPriority="null"
          value={finalValue}
          tagName={tag.displayName}
        />
      );
    } else if (!isColorCodingFalse && !alarmPriorityTag) {
      ComponentToRender = (
        <LabelTagBox
          isLiftStationMobile={isLiftStationMobile}
          alarmPriority="Cleared"
          value={finalValue}
          tagName={tag.displayName}
        />
      );
    } else if (isNotRestOfTheTags && alarmPriorityTag) {
      ComponentToRender = (
        <LabelTagBox
          isLiftStationMobile={isLiftStationMobile}
          alarmPriority={alarmPriorityTag}
          value={finalValue}
          tagName={tag.displayName}
        />
      );
    } else {
      ComponentToRender = (
        <LabelTagBox
          isLiftStationMobile={isLiftStationMobile}
          tagName={tag.displayName}
          alarmPriority="null"
          value={finalValue}
        />
      );
    }

    return (
      <StyledTagItem
        theme={theme}
        isMobile={isMobile}
        data-testid={testId}
        isLiveData={isLiveDataWidget}
        isLiftStation={isLiftStation}
        isLiftStationMobile={isLiftStationMobile}
        isSCADA={isSCADA}
      >
        <Box sx={{ minWidth: 0, flex: '0 1 auto' }}>
          <Tooltip
            title={displayNameAndRawUnit}
            componentsProps={{ tooltip: { sx: { bgcolor: '#fff' } } }}
          >
            <StyledItemText
              isMobile={isMobile}
              isLiveData={isLiveDataWidget}
              isLiftStationMobile={isLiftStationMobile}
              isLiftStation={isLiftStation}
            >
              {displayNameAndRawUnit}
            </StyledItemText>
          </Tooltip>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexShrink: 0,
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {isOnlyLevel && (
            <Box
              sx={{ display: 'flex', alignItems: 'center' }}
              data-sm={trend === 'up' ? 'up' : 'down'}
            >
              {trend === 'up' && <ArrowDropUp sx={{ color: greenColor, fontSize: 22 }} />}
              {trend === 'down' && <ArrowDropDown sx={{ color: redColor, fontSize: 22 }} />}
            </Box>
          )}
          {ComponentToRender}
        </Box>
      </StyledTagItem>
    );
  }
);
